// AttractionView.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../../axiosConfig";
import { ArrowLeft, Eye, EyeOff, ImageOff, Calendar } from "lucide-react";

// ============================================================================
// Small reusable UI
// ============================================================================
const Button = ({
  children,
  onClick,
  variant = "primary",
  size = "md",
  className = "",
  icon: Icon,
  disabled,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200";
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white shadow-sm",
    secondary: "bg-gray-100 hover:bg-gray-200 text-gray-700",
    danger: "bg-red-600 hover:bg-red-700 text-white",
    ghost: "hover:bg-gray-100 text-gray-600",
    success: "bg-green-600 hover:bg-green-700 text-white",
  };
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className} ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      }`}
      {...props}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
};

const Card = ({ children, className = "" }) => {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      {children}
    </div>
  );
};

// ============================================================================
// MultiSelectDropdown - VIEWABLE (opens), but READ-ONLY (no changes)
// - Shows selected labels, not "X selected"
// ============================================================================
const MultiSelectDropdown = ({
  label,
  options,
  selectedValues,
  onChange, // not used in view mode
  placeholder = "— Select —",
  viewOnly = false, // ✅ new
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedSet = useMemo(() => new Set(selectedValues || []), [selectedValues]);
  const selectedLabels = useMemo(() => {
    const map = new Map((options || []).map((o) => [String(o.value), o.label]));
    const labels = (selectedValues || [])
      .map((v) => map.get(String(v)))
      .filter(Boolean);
    return labels;
  }, [options, selectedValues]);

  // ✅ show actual names, not "X selected"
  const displayText =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length <= 3
      ? selectedLabels.join(", ")
      : `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2} more`;

  const toggleOption = (value) => {
    if (viewOnly) return; // ✅ prevent changes
    const v = String(value);
    const newValues = selectedSet.has(v)
      ? selectedValues.filter((x) => String(x) !== v)
      : [...selectedValues, v];
    onChange(newValues);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block mb-2 text-sm font-medium text-gray-700">{label}</label>

      {/* ✅ Always clickable to open (viewOnly still opens) */}
      <div
        className={`flex items-center justify-between w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          viewOnly ? "bg-gray-50" : "bg-white"
        } cursor-pointer`}
        onClick={() => setIsOpen((s) => !s)}
        title={selectedLabels.join(", ")}
      >
        <span className={selectedLabels.length === 0 ? "text-gray-400" : "text-gray-900"}>
          {displayText}
        </span>
        <span className="text-gray-400">▼</span>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg max-h-60">
          {options.map((option) => {
            const checked = selectedSet.has(String(option.value));
            return (
              <label
                key={option.value}
                className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-50"
                onClick={(e) => {
                  // allow clicking row to toggle only if not viewOnly
                  e.preventDefault();
                  if (!viewOnly) toggleOption(option.value);
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly={viewOnly}
                  onChange={() => {
                    if (!viewOnly) toggleOption(option.value);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>

                {viewOnly && checked && (
                  <span className="ml-auto text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
                    Selected
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Settings Form (View-only but dropdowns OPENABLE)
// ============================================================================
const CarouselSettingsFormView = ({ settings, meta, loading }) => {
  const {
    blockName = "",
    subHeader = "",
    selectedCategories = [],   // subCategory IDs (strings)
    selectedCities = [],       // city IDs (strings)
    selectedTags = [],         // tag codes (strings)
    selectedCustomPage = "",
    itemsPerBlock = 8,
    displayOrder = 1,
  } = settings || {};

  if (loading) {
    return (
      <Card className="p-6 mb-8">
        <h3 className="mb-4 text-xl font-semibold">Carousel Block Settings</h3>
        <div className="py-8 text-center text-gray-500">Loading options from server...</div>
      </Card>
    );
  }

  return (
    <Card className="p-6 mb-8">
      <h3 className="mb-6 text-xl font-semibold text-gray-900">Carousel Block Settings (View)</h3>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Block Name</label>
          <input
            disabled
            type="text"
            value={blockName}
            className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg opacity-80"
          />
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Sub Header</label>
          <input
            disabled
            type="text"
            value={subHeader}
            className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg opacity-80"
          />
        </div>

        {/* ✅ View-only but OPENABLE, shows selected by name */}
        <MultiSelectDropdown
          viewOnly
          label="Category (Sub Categories)"
          options={meta.subCategories}
          selectedValues={selectedCategories}
          onChange={() => {}}
          placeholder="— All Categories —"
        />

        <MultiSelectDropdown
          viewOnly
          label="City"
          options={meta.cities}
          selectedValues={selectedCities}
          onChange={() => {}}
          placeholder="— All Cities —"
        />

        <MultiSelectDropdown
          viewOnly
          label="Tag"
          options={meta.tags}
          selectedValues={selectedTags}
          onChange={() => {}}
          placeholder="— All Tags —"
        />

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Custom Page</label>
          <select
            disabled
            value={selectedCustomPage}
            className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg opacity-80"
          >
            <option value="">
              {selectedCustomPage ? `Page ID: ${selectedCustomPage}` : "— Select Custom Page —"}
            </option>
            {meta.customPages.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Items per Block</label>
          <input
            disabled
            type="number"
            value={itemsPerBlock}
            className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg opacity-80"
          />
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Display Order</label>
          <input
            disabled
            type="number"
            value={displayOrder}
            className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg opacity-80"
          />
        </div>
      </div>
    </Card>
  );
};

// ============================================================================
// Carousel Items (view only)
// ============================================================================
const CarouselItemsView = ({ title, visible, items }) => {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{items.length} item(s)</p>
        </div>

        {/* <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-sm text-gray-700">
          {visible ? (
            <>
              <Eye size={16} /> Visible
            </>
          ) : (
            <>
              <EyeOff size={16} /> Hidden
            </>
          )}
        </div> */}
      </div>

      {items.length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          <p>No items found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {items.map((item) => {
            const attraction = item.selectedItem;
            const imageUrl = attraction?.imageUrl || null;
            const loadingImage = attraction?.loadingImage || false;

            return (
              <div
                key={item.id}
                className="relative overflow-hidden transition-all duration-200 bg-white border border-gray-200 rounded-lg shadow-sm group hover:shadow-md hover:-translate-y-1"
                title={attraction?.name || ""}
              >
                <div className="overflow-hidden bg-gray-100 aspect-square">
                  {loadingImage ? (
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                  ) : imageUrl ? (
                    <img src={imageUrl} alt={attraction.name} className="object-cover w-full h-full" />
                  ) : (
                    <div className="flex flex-col items-center justify-center w-full h-full text-gray-400 bg-gray-200 border-2 border-dashed rounded-lg">
                      <ImageOff size={24} />
                      <span className="mt-2 text-xs">No Image</span>
                    </div>
                  )}
                </div>

                <div className="p-3">
                  <h4 className="text-sm font-semibold text-center text-gray-900 truncate">
                    {attraction?.name || "-"}
                  </h4>

                  {/* <p className="mt-1 text-xs text-center text-gray-500">
                    <Calendar className="inline-block w-3 h-3 mr-1 -mt-0.5" />
                    Valid: —
                  </p> */}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// Destinations Grid (view only)
// - NOTE: list API expects CATEGORY IDs (not subCategory IDs)
// ============================================================================
const AttractionsGridViewOnly = ({ categoryIds = [], cityIds = [], tagIds = [] }) => {
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const fetchAttractions = async () => {
    try {
      setLoading(true);
      setError(null);

      const start = (currentPage - 1) * itemsPerPage;

      const paramsObj = { start, length: itemsPerPage };
      if (categoryIds?.length > 0) paramsObj["categoryIds[]"] = categoryIds;
      if (cityIds?.length > 0) paramsObj["cityIds[]"] = cityIds;
      if (tagIds?.length > 0) paramsObj["tagIds[]"] = tagIds;

      const response = await axiosInstance.get(
        "/internal-api/v1/client-web/destination-panel/config/list",
        {
          params: paramsObj,
          paramsSerializer: (params) =>
            Object.keys(params)
              .map((key) => {
                const val = params[key];
                if (Array.isArray(val))
                  return val.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join("&");
                return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
              })
              .join("&"),
        }
      );

      if (response.data?.data) {
        const rawItems = response.data.data.filter((item) => item.isActive);

        const formatted = rawItems.map((item) => ({
          id: item.id,
          uuid: item.uuid,
          name: item.name,
          city: item.cityName || "Unknown",
          imageUrl: null,
          loadingImage: true,
        }));

        setAttractions(formatted);
        setTotalItems(response.data.recordsTotal || 0);

        formatted.forEach((attr) => fetchImageForAttraction(attr));
      } else {
        setAttractions([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error("Error fetching destinations:", err);
      setError("Failed to load attractions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchImageForAttraction = async (attraction) => {
    if (!attraction.uuid) return;

    try {
      const response = await axiosInstance.get(
        `/internal-api/v1/client-web/destination-panel/config/${attraction.uuid}/view`
      );

      if (response.data?.result?.images?.length > 0) {
        const sortedImages = response.data.result.images.sort(
          (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
        );
        const firstImage = sortedImages[0].image;
        const baseUrl = response.data.result.localImageServer || "";
        const imagePath = firstImage.path || "";
        const fullUrl = baseUrl + imagePath;

        setAttractions((prev) =>
          prev.map((a) =>
            a.id === attraction.id ? { ...a, imageUrl: fullUrl, loadingImage: false } : a
          )
        );
      } else {
        setAttractions((prev) =>
          prev.map((a) => (a.id === attraction.id ? { ...a, loadingImage: false } : a))
        );
      }
    } catch (err) {
      setAttractions((prev) =>
        prev.map((a) => (a.id === attraction.id ? { ...a, loadingImage: false } : a))
      );
    }
  };

  useEffect(() => {
    fetchAttractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, categoryIds.join(","), cityIds.join(","), tagIds.join(",")]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="p-6 bg-white border border-gray-200 shadow-sm rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">All Destinations (View Only)</h3>
          <p className="text-sm text-gray-500">Loaded from database</p>
        </div>
        <span className="bg-gray-100 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600">
          {attractions.length} of {totalItems} Attractions
        </span>
      </div>

      {error && <div className="mb-4 text-sm text-center text-red-600">{error}</div>}

      {loading ? (
        <div className="py-12 text-center text-gray-500">Loading attractions...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {attractions.map((attraction) => (
              <div
                key={attraction.id}
                className="relative overflow-hidden transition-all duration-200 bg-white border border-gray-200 rounded-lg shadow-sm group hover:shadow-md hover:-translate-y-1"
              >
                <div className="overflow-hidden bg-gray-100 aspect-square">
                  {attraction.loadingImage ? (
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                  ) : attraction.imageUrl ? (
                    <img src={attraction.imageUrl} alt={attraction.name} className="object-cover w-full h-full" />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full text-xs text-gray-400 bg-gray-200 border-2 border-dashed rounded-lg">
                      No Image
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-center text-gray-900 truncate">{attraction.name}</h4>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// MAIN VIEW PAGE
// ============================================================================
const AttractionView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const blockId = params.get("blockId");

  const [loading, setLoading] = useState(true);
  const [metaLoading, setMetaLoading] = useState(true);
  const [error, setError] = useState(null);

  const [carouselVisible, setCarouselVisible] = useState(true);
  const [carouselTitle, setCarouselTitle] = useState("Destination Carousel");
  const [carouselItems, setCarouselItems] = useState([]);

  const [settings, setSettings] = useState({
    blockName: "",
    subHeader: "",
    selectedCategories: [],   // subCategory IDs (strings)
    selectedCategoryIds: [],  // category IDs (strings) used for destination list filtering
    selectedCities: [],       // city IDs (strings)
    selectedTags: [],         // tag codes (strings)
    selectedCustomPage: "",
    itemsPerBlock: 8,
    displayOrder: 1,
  });

  const [meta, setMeta] = useState({
    subCategories: [], // value = subCategoryId (string)
    cities: [],        // value = cityId (string)
    tags: [],          // value = tagCode (string)
    customPages: [],
  });

  // 1) Load meta
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        setMetaLoading(true);

        const [metaRes, pagesRes] = await Promise.all([
          axiosInstance.get("/internal-api/v1/client-web/cms/page/page-blocks/meta"),
          axiosInstance.get("/internal-api/v1/client-web/cms/custom-page/list?start=0&length=100"),
        ]);

        const result = metaRes.data?.result;

        const subCategories = Array.isArray(result?.subCategories)
          ? result.subCategories.map((item) => ({
              value: String(item.id),
              label: item.name || item.code || `SubCategory ${item.id}`,
            }))
          : [];

        const cities = Array.isArray(result?.cities)
          ? result.cities.map((item) => ({
              value: String(item.id),
              label: item.name || item.code || `City ${item.id}`,
            }))
          : [];

        const tags = Array.isArray(result?.destinationTags)
          ? result.destinationTags
              .filter((item) => item.code && item.name && item.name.trim() !== "")
              .map((item) => ({
                value: String(item.code), // tagCode
                label: item.name.trim(),
              }))
          : [];

        const customPages = Array.isArray(pagesRes.data?.data)
          ? pagesRes.data.data
              .filter((p) => p.isActive)
              .map((p) => ({
                value: String(p.id),
                label: p.pageMeta || p.name || `Page ${p.id}`,
              }))
          : [];

        setMeta({ subCategories, cities, tags, customPages });
      } catch (e) {
        console.error("Failed to load meta:", e);
      } finally {
        setMetaLoading(false);
      }
    };

    fetchMeta();
  }, []);

  // Helper function to fetch image for a carousel item
  const fetchImageForCarouselItem = async (item) => {
    const uuid = item?.selectedItem?.uuid;
    if (!uuid) return;

    try {
      const response = await axiosInstance.get(
        `/internal-api/v1/client-web/destination-panel/config/${uuid}/view`
      );

      if (response.data?.result?.images?.length > 0) {
        const sortedImages = response.data.result.images.sort(
          (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
        );
        const firstImage = sortedImages[0].image;
        const baseUrl = response.data.result.localImageServer || "";
        const imagePath = firstImage.path || "";
        const fullUrl = baseUrl + imagePath;

        setCarouselItems((prev) =>
          prev.map((carouselItem) =>
            carouselItem.id === item.id
              ? {
                  ...carouselItem,
                  selectedItem: {
                    ...carouselItem.selectedItem,
                    imageUrl: fullUrl,
                    loadingImage: false,
                  },
                }
              : carouselItem
          )
        );
      } else {
        setCarouselItems((prev) =>
          prev.map((carouselItem) =>
            carouselItem.id === item.id
              ? {
                  ...carouselItem,
                  selectedItem: {
                    ...carouselItem.selectedItem,
                    loadingImage: false,
                  },
                }
              : carouselItem
          )
        );
      }
    } catch (err) {
      console.error("Error fetching image for carousel item:", err);
      setCarouselItems((prev) =>
        prev.map((carouselItem) =>
          carouselItem.id === item.id
            ? {
                ...carouselItem,
                selectedItem: {
                  ...carouselItem.selectedItem,
                  loadingImage: false,
                },
              }
            : carouselItem
        )
      );
    }
  };

  // 2) Load view block (UPDATED mapping)
  useEffect(() => {
    const fetchBlock = async () => {
      if (!blockId) {
        setError("Missing blockId in URL");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const res = await axiosInstance.get(
          `/internal-api/v1/client-web/cms/page/page-blocks/${blockId}/view`
        );

        const result = res.data?.result;
        const data = result?.data;

        const title = data?.title || "Destination Carousel";
        const subHeader = data?.subTitle || "";
        const isVisible = !!data?.isVisible;

        const carousel = data?.carousels?.[0];
        const maxItems = Number(carousel?.maxItems || 8);
        const displayOrder = Number(result?.displayOrder || 1);

        // ✅ categorySubCategoryId -> meta.subCategories[].id
        const categorySybCategories = Array.isArray(carousel?.categorySybCategories)
          ? carousel.categorySybCategories
          : [];

        // IMPORTANT: in your example `categorySubCategoryId` exists.
        // Some responses also include nested objects. We'll support both.
        const selectedCategories = categorySybCategories
          .map((x) => x?.categorySubCategoryId ?? x?.subCategory?.id)
          .filter((v) => v != null)
          .map((v) => String(v)); // subCategory IDs

        const selectedCategoryIds = Array.from(
          new Set(
            categorySybCategories
              .map((x) => x?.category?.id)
              .filter((v) => v != null)
              .map((v) => String(v))
          )
        );

        const selectedCities = Array.isArray(carousel?.cities)
          ? carousel.cities
              .map((x) => x?.cityId)
              .filter((v) => v != null)
              .map((v) => String(v))
          : [];

        const selectedTags = Array.isArray(carousel?.blockTags)
          ? carousel.blockTags
              .map((x) => x?.tagCode)
              .filter(Boolean)
              .map((v) => String(v))
          : [];

        const cards = Array.isArray(carousel?.cards) ? carousel.cards : [];
        const mappedItems = cards.map((c) => {
          const d = c?.destinationCard?.destination;
          const name = (d?.displayName || d?.name || "").trim() || "-";
          return {
            id: c?.id ?? Math.random(),
            selectedItem: {
              id: d?.id,
              uuid: d?.uuid,
              name,
              city: d?.cityId ? `City ${d.cityId}` : "Unknown",
              imageUrl: d?.webImageUrl || null,
              loadingImage: true,
            },
          };
        });

        setCarouselTitle(title);
        setCarouselVisible(isVisible);
        setCarouselItems(mappedItems);

        // Fetch images for each carousel item
        mappedItems.forEach((item) => fetchImageForCarouselItem(item));

        setSettings({
          blockName: title,
          subHeader,
          selectedCategories,
          selectedCategoryIds,
          selectedCities,
          selectedTags,
          selectedCustomPage: data?.viewMoreLinkPageId ? String(data.viewMoreLinkPageId) : "",
          itemsPerBlock: maxItems,
          displayOrder,
        });
      } catch (e) {
        console.error("Failed to load view block:", e);
        setError("Failed to load view block.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlock();
  }, [blockId]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4 mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Attraction Carousel - View</h1>
              <p className="mt-1 text-sm text-gray-500">Dropdowns are viewable (open), but not editable</p>
            </div>

            <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/LandingPage")}>
              Back to Landing Page
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 py-8 mx-auto max-w-7xl">
        {error && (
          <Card className="p-6 mb-6 border-red-200">
            <div className="text-red-600">{error}</div>
          </Card>
        )}

        {loading ? (
          <Card className="p-6">
            <div className="py-10 text-center text-gray-500">Loading block...</div>
          </Card>
        ) : (
          <>
            <CarouselSettingsFormView settings={settings} meta={meta} loading={metaLoading} />

            <div className="space-y-8">
              <CarouselItemsView
                title={`${carouselTitle} (View Only)`}
                visible={carouselVisible}
                items={carouselItems}
              />
            </div>

            <div className="mt-10">
              <AttractionsGridViewOnly
                categoryIds={settings.selectedCategoryIds} // CATEGORY IDs for list API
                cityIds={settings.selectedCities}
                tagIds={settings.selectedTags}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AttractionView;
